import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type ProcessType = 'upload' | 'generate' | 'generic';

export interface ProcessInfo {
  label?: string;
  sublabel?: string;
  progress?: number;
  status?: 'running' | 'completed' | 'failed';
}

export interface ProcessEntry extends ProcessInfo {
  id: string;
  type: ProcessType;
  createdAt: number;
}

interface ProcessingOverlayContextValue {
  // True if there are any active long processes
  isProcessing: boolean;
  // True if overlay should be visible (processing and not manually hidden)
  isVisible: boolean;
  // Currently active process types (for future granularity)
  activeTypes: Set<ProcessType>;
  // Counts per process type
  processCounts: Record<ProcessType, number>;
  // Detailed list of active processes
  activeProcesses: ProcessEntry[];
  // Start/end a process (increments/decrements counter)
  startProcess: (type?: ProcessType, info?: ProcessInfo) => string;
  updateProcess: (id: string, info: ProcessInfo) => void;
  endProcess: (idOrType?: string | ProcessType) => void;
  // Manually hide/show overlay
  hideOverlay: () => void;
  showOverlay: () => void;
}

const ProcessingOverlayContext = createContext<ProcessingOverlayContextValue | undefined>(
  undefined,
);

export const ProcessingOverlayProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activeCount, setActiveCount] = useState(0);
  const [processCounts, setProcessCounts] = useState<Record<ProcessType, number>>({
    upload: 0,
    generate: 0,
    generic: 0,
  });
  const [manuallyHidden, setManuallyHidden] = useState(false);
  const [processes, setProcesses] = useState<ProcessEntry[]>([]);

  // Track outstanding calls to avoid negative counts on double finally paths
  const counterReference = useRef(0);

  const startProcess = useCallback((type: ProcessType = 'generic', info?: ProcessInfo) => {
    counterReference.current += 1;
    const id = `${Date.now()}-${counterReference.current}-${Math.random()
      .toString(36)
      .slice(2, 7)}`;
    const entry: ProcessEntry = {
      id,
      type,
      createdAt: Date.now(),
      label: info?.label,
      sublabel: info?.sublabel,
      progress: info?.progress,
      status: info?.status,
    };
    setActiveCount((c) => c + 1);
    setProcessCounts((previous) => {
      const next = { ...previous };
      next[type] = (next[type] ?? 0) + 1;
      return next;
    });
    setProcesses((previous) => [...previous, entry]);
    // On new process, unhide if previously hidden for a past session
    setManuallyHidden(false);
    return id;
  }, []);

  const endProcess = useCallback((idOrType: string | ProcessType = 'generic') => {
    setProcesses((previous) => {
      let removedType: ProcessType | undefined;
      if (typeof idOrType === 'string' && idOrType.includes('-')) {
        const index = previous.findIndex((p) => p.id === idOrType);
        if (index === -1) {
          return previous;
        }
        removedType = previous[index].type;
        const next = [...previous];
        next.splice(index, 1);
        if (counterReference.current > 0) {
          counterReference.current -= 1;
        }
        setActiveCount((c) => Math.max(0, c - 1));
        setProcessCounts((pc) => ({
          ...pc,
          [removedType as ProcessType]: Math.max(0, (pc[removedType as ProcessType] ?? 0) - 1),
        }));
        return next;
      }
      // Fallback: remove first process of the given type
      const type = idOrType as ProcessType;
      const index = previous.findIndex((p) => p.type === type);
      if (index === -1) {
        return previous;
      }
      removedType = type;
      setProcessCounts((pc) => ({ ...pc, [type]: Math.max(0, (pc[type] ?? 0) - 1) }));
      if (counterReference.current > 0) {
        counterReference.current -= 1;
      }
      setActiveCount((c) => Math.max(0, c - 1));
      const next = [...previous];
      next.splice(index, 1);
      return next;
    });
  }, []);

  const updateProcess = useCallback((id: string, info: ProcessInfo) => {
    setProcesses((previous) =>
      previous.map((process) =>
        process.id === id
          ? {
              ...process,
              ...info,
            }
          : process,
      ),
    );
  }, []);

  const hideOverlay = useCallback(() => setManuallyHidden(true), []);
  const showOverlay = useCallback(() => setManuallyHidden(false), []);

  const isProcessing = activeCount > 0;
  const isVisible = isProcessing && !manuallyHidden;

  // Keep activeTypes in sync with processCounts
  const syncedActiveTypes = useMemo(() => {
    const set = new Set<ProcessType>();
    for (const k of Object.keys(processCounts) as ProcessType[]) {
      if (processCounts[k] > 0) {
        set.add(k);
      }
    }
    return set;
  }, [processCounts]);

  const value = useMemo(
    () => ({
      isProcessing,
      isVisible,
      activeTypes: syncedActiveTypes,
      processCounts,
      activeProcesses: processes,
      startProcess,
      updateProcess,
      endProcess,
      hideOverlay,
      showOverlay,
    }),
    [
      isProcessing,
      isVisible,
      syncedActiveTypes,
      processCounts,
      processes,
      startProcess,
      updateProcess,
      endProcess,
      hideOverlay,
      showOverlay,
    ],
  );

  return (
    <ProcessingOverlayContext.Provider value={value}>{children}</ProcessingOverlayContext.Provider>
  );
};

export const useProcessingOverlay = (): ProcessingOverlayContextValue => {
  const context = useContext(ProcessingOverlayContext);
  if (!context) {
    throw new Error('useProcessingOverlay must be used within a ProcessingOverlayProvider');
  }
  return context;
};
